"use client";

import { useRef, useEffect, type RefObject, useContext } from "react";

import {
  ContentMenuContext,
  type ContentMenuSection,
  type ContentMenuContextState,
} from "../contexts/ContentMenuContext";

export type UseContentMenuSectionProps = Pick<
  ContentMenuSection,
  "id" | "label" | "index" | "icon" | "disable" | "forcePosition"
>;

/**
 * Hook to register a section in the content menu
 * Automatically registers on mount and unregisters on unmount
 * @param props - Section configuration with id and label
 * @returns A ref to attach to the section element
 */
export const useContentMenuSection = <T extends HTMLElement = HTMLElement>({
  id,
  label,
  index,
  icon,
  disable,
  forcePosition,
}: UseContentMenuSectionProps): RefObject<T | null> => {
  const ref = useRef<T>(null);
  const contentMenu = useMaybeContentMenu();

  // Mount/unmount only — DO NOT add props like disable, index, icon,
  // forcePosition here. Unregister→re-register causes ContentMenu
  // sections to momentarily disappear, triggering a full tree re-render
  // (e.g. card open/close on inner-routed pages flashes the whole page).
  useEffect(() => {
    if (!contentMenu) return;

    contentMenu.registerSection({
      id,
      label,
      index,
      icon,
      ref: ref as RefObject<HTMLElement | null>,
      disable,
      forcePosition,
    });

    return () => {
      contentMenu.unregisterSection(id);
    };
  }, [id, label]);

  // Property updates — runs when section props change without
  // unmounting. registerSection is an upsert (Map.set) so calling it
  // with the same id overwrites the entry and batches a single flush.
  useEffect(() => {
    if (!contentMenu) return;

    contentMenu.registerSection({
      id,
      label,
      index,
      icon,
      ref: ref as RefObject<HTMLElement | null>,
      disable,
      forcePosition,
    });
  }, [index, icon, disable, forcePosition]);

  return ref;
};

/**
 * Hook to access the ContentMenu context
 * Returns undefined if used outside of ContentMenuProvider (won't throw)
 * @returns The content menu context state or undefined
 */
export const useMaybeContentMenu = (): ContentMenuContextState | null =>
  useContext(ContentMenuContext);
